import "bootstrap/dist/css/bootstrap.min.css";
import { onValue, ref, update, remove, push } from "firebase/database";
import { useEffect, useState } from "react";
import { db } from "../Firebase";

const EditApproval = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editingMember, setEditingMember] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [reason, setReason] = useState('');
  const [confirmAction, setConfirmAction] = useState({ show: false, type: '', message: '' });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [urbanAreas, setUrbanAreas] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [mandirs, setMandirs] = useState([]);
  const [jatiPrakars, setJatiPrakars] = useState([]);
  const [upjatiPrakars, setUpjatiPrakars] = useState([]);
  const [gotras, setGotras] = useState([]);
  const [moors, setMoors] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [grams, setGrams] = useState([]);
  const [editingBasic, setEditingBasic] = useState(false);
  const [basicData, setBasicData] = useState({});

  const relationshipOptions = [
    "मुखिया स्वयं", "पत्नी", "पुत्र", "पुत्री", "बहू", "सगी बहन", "पारिवारिक बहन",
    "सगा भाई", "पारिवारिक भाई", "दादा", "दादी", "परदादा", "परदादी", "चाचा", "चाची",
    "बड़े पापा", "बड़ी मम्मी", "बुआ", "फूपा", "नाना", "नानी", "मामा", "मामी", "मोसी",
    "मोसिया", "माँ", "पिता", "सास", "ससुर", "नाती", "नातिन", "पोता", "पोती",
    "भतीजा", "भतीजी", "भांजा", "भांजी", "देवरानी", "जिठानी", "देवर", "जेठ",
    "सरुभाई", "जीजा", "भाभी", "साला", "साली", "साराज", "दामाद", "व्यावहारिक रिश्ता", "अन्य"
  ];

  useEffect(() => {
    const regRef = ref(db, "registration");
    const unsubscribe = onValue(regRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allRegs = Object.entries(data)
          .map(([id, regData]) => ({ id, ...regData }))
          .sort((a, b) => (b.meta?.createdAt || 0) - (a.meta?.createdAt || 0));
        setRegistrations(allRegs);
        
        if (selectedFamily) {
          const updated = allRegs.find(r => r.id === selectedFamily.id);
          if (updated) {
            setSelectedFamily(updated);
          }
        }
      } else {
        setRegistrations([]);
      }
      setLoading(false);
    });

    // Load master data
    const loadMasters = (path, setter) =>
      onValue(ref(db, path), snap =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : []
        )
      );

    loadMasters("masters/states", setStates);
    loadMasters("masters/cities", setCities);
    loadMasters("masters/urbanAreas", setUrbanAreas);
    loadMasters("masters/workTypes", setWorkTypes);
    loadMasters("masters/jainMandir", setMandirs);
    loadMasters("masters/jatiPrakars", setJatiPrakars);
    loadMasters("masters/upjatiPrakars", setUpjatiPrakars);
    loadMasters("masters/gotras", setGotras);
    loadMasters("masters/moors", setMoors);
    loadMasters("masters/blocks", setBlocks);
    loadMasters("masters/panchayats", setPanchayats);
    loadMasters("masters/grams", setGrams);

    return () => unsubscribe();
  }, [selectedFamily?.id]);

  const handleApproveMember = async (memberId) => {
    setConfirmAction({ 
      show: true, 
      type: 'approve_member', 
      message: `Approve member: ${selectedFamily.members[memberId].name}?`,
      memberId: memberId
    });
  };

  const handleRejectMember = (memberId) => {
    setConfirmAction({
      show: true,
      type: 'reject_member',
      message: `Permanently delete member: ${selectedFamily.members[memberId].name}? This cannot be undone.`,
      memberId: memberId
    });
  };

  const confirmRejectMember = async () => {
    try {
      await remove(ref(db, `registration/${selectedFamily.id}/members/${confirmAction.memberId}`));
      setConfirmAction({ show: false, type: '', message: '' });
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const confirmApproveMember = async () => {
    try {
      await update(ref(db), {
        [`registration/${selectedFamily.id}/members/${confirmAction.memberId}/approved`]: true,
        [`registration/${selectedFamily.id}/members/${confirmAction.memberId}/approvedAt`]: Date.now()
      });
      setConfirmAction({ show: false, type: '', message: '' });
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleApproveFamily = async () => {
    const allApproved = Object.values(selectedFamily.members || {}).every(m => m.approved);
    if (!allApproved) {
      alert('Please approve all members first!');
      return;
    }
    try {
      await update(ref(db), {
        [`registration/${selectedFamily.id}/meta/status`]: 'approved',
        [`registration/${selectedFamily.id}/meta/approvedAt`]: Date.now()
      });
      setConfirmAction({ show: false, type: '', message: '' });
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleAction = async (type) => {
    try {
      if (type === 'reject') {
        await push(ref(db, 'rejectedRegistrations'), {
          headName: selectedFamily.basic?.headName || '',
          mobile: selectedFamily.basic?.mobile || selectedFamily.meta?.contactNumber || '',
          reason: reason,
          rejectedAt: Date.now(),
        });
        await remove(ref(db, `registration/${selectedFamily.id}`));
        setSelectedFamily(null);
      } else {
        const updates = {
          [`registration/${selectedFamily.id}/meta/status`]: type,
          [`registration/${selectedFamily.id}/meta/updatedAt`]: Date.now()
        };
        if (type === 'hold' && reason) updates[`registration/${selectedFamily.id}/meta/holdReason`] = reason;
        if (type === 'request_changes' && reason) updates[`registration/${selectedFamily.id}/meta/changeRequest`] = reason;
        await update(ref(db), updates);
      }
      setConfirmAction({ show: false, type: '', message: '' });
      setReason('');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleUnhold = async () => {
    try {
      await update(ref(db), {
        [`registration/${selectedFamily.id}/meta/status`]: 'pending',
        [`registration/${selectedFamily.id}/meta/holdReason`]: null
      });
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleEditBasic = () => {
    setBasicData({ ...selectedFamily.basic });
    setEditingBasic(true);
  };

  const handleSaveBasic = async () => {
    try {
      await update(ref(db), { [`registration/${selectedFamily.id}/basic`]: basicData });
      setEditingBasic(false);
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const setBasic = (key, value) => setBasicData(prev => ({ ...prev, [key]: value }));

  const handleEditMember = (memberId, memberData) => {
    setEditingMember(memberId);
    setEditedData({...memberData});
  };

  const handleSaveMember = async (memberId) => {
    try {
      const updates = {};
      Object.keys(editedData).forEach(key => {
        updates[`registration/${selectedFamily.id}/members/${memberId}/${key}`] = editedData[key];
      });
      await update(ref(db), updates);
      setEditingMember(null);
      setEditedData({});
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const filteredRegs = registrations.filter(reg => {
    const status = reg.meta?.status || 'pending';
    if (filter === 'all') return true;
    if (filter === 'hold') return status === 'hold';
    return status === filter;
  });

  if (loading) {
    return (
      <div className="container my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (selectedFamily) {
    const status = selectedFamily.meta?.status || 'pending';
    const isApproved = status === 'approved';
    const isHold = status === 'hold';
    const allMembersApproved = Object.values(selectedFamily.members || {}).every(m => m.approved);
    
    return (
      <div className="container-fluid my-4" style={{ maxWidth: '1400px' }}>
        <button className="btn btn-outline-secondary mb-3" onClick={() => setSelectedFamily(null)} style={{ fontWeight: '500' }}>← Back to List</button>
        
        {/* Hold Warning */}
        {isHold && selectedFamily.meta?.holdReason && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center" style={{ fontSize: '0.95rem' }}>
            <div><strong>⏸ On Hold:</strong> {selectedFamily.meta.holdReason}</div>
            <button className="btn btn-sm btn-success" onClick={handleUnhold} style={{ fontWeight: '500' }}>Remove Hold</button>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="card mb-3 shadow-sm">
          <div className="card-body">
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-success" onClick={() => setConfirmAction({ show: true, type: 'approve', message: 'Approve this family?' })} disabled={!allMembersApproved || isApproved} style={{ fontWeight: '500' }}>✅ Approve Family</button>
              <button className="btn btn-warning" onClick={() => setConfirmAction({ show: true, type: 'request_changes', message: 'Request changes?' })} disabled={isApproved} style={{ fontWeight: '500' }}>⚠ Request Changes</button>
              <button className="btn btn-secondary" onClick={() => setConfirmAction({ show: true, type: 'hold', message: 'Put on hold?' })} disabled={isApproved} style={{ fontWeight: '500' }}>⏸ Hold</button>
              <button className="btn btn-danger" onClick={() => setConfirmAction({ show: true, type: 'reject', message: 'Reject this family?' })} disabled={isApproved} style={{ fontWeight: '500' }}>❌ Reject</button>
              <button className="btn btn-success" onClick={() => {
                const message = `जय जिनेन्द्र 🙏
आपने जैन समग्र जनगणना में अपने परिवार का पंजीयन किया है उसके लिए धन्यबाद 🤝

परन्तु आपके द्वारा परिवार के सभी सदस्य की जानकारी नहीं भरी गई है
अतः आपसे निवेदन है की नीचे दी गई लिंक पर आप अपने परिवार के सभी सदस्य की सभी जानकारी भरे 👇

https://jainsamagra.com/information/${selectedFamily.id}

यदि पंजीयन करने में कोई परेशानी हो रही हो तो हमें जानकारी दे हम आपकी पूरी मदद करेगे 
(परिवार की जानकारी पूर्ण होने पर हमें मेसेज कर दे)

धन्यबाद 
जैन समग्र`;
                const phone = (selectedFamily.basic?.mobile || selectedFamily.meta?.contactNumber || '').replace(/\D/g, '');
                const waLink = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;
                window.location.href = waLink;
              }} style={{ fontWeight: '500', backgroundColor: '#25D366', borderColor: '#25D366', color: 'white' }}>📱 WhatsApp</button>
            </div>
            {!allMembersApproved && <small className="text-danger d-block mt-2" style={{ fontSize: '0.9rem' }}>* Approve all members first to approve family</small>}
          </div>
        </div>

        {/* Family Info */}
        <div className="card mb-3 shadow-sm">
          <div className="card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '1rem' }}>
            <h5 className="mb-0 text-white fw-bold">Family: {selectedFamily.basic?.headName} (ID: {selectedFamily.id})</h5>
          </div>
          <div className="card-body" style={{ fontSize: '0.95rem' }}>
            <div className="row g-3">
              <div className="col-md-3"><strong>Mobile:</strong> {selectedFamily.basic?.mobile}</div>
              <div className="col-md-3"><strong>State:</strong> {selectedFamily.basic?.state}</div>
              <div className="col-md-3"><strong>City:</strong> {selectedFamily.basic?.city}</div>
              <div className="col-md-3"><strong>Status:</strong> <span className={`badge bg-${isApproved ? 'success' : isHold ? 'secondary' : 'warning'}`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>{status}</span></div>
            </div>
          </div>
        </div>
        
        {/* Family Basic Info */}
        <div className="card mb-3 shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#1e293b', padding: '0.75rem 1rem' }}>
            <h6 className="mb-0 fw-bold text-white">📋 परिवार की मूल जानकारी</h6>
            {editingBasic ? (
              <div className="d-flex gap-2">
                <button className="btn btn-sm" style={{ border: '1px solid white', color: 'white', background: 'transparent' }} onClick={handleSaveBasic}>Save</button>
                <button className="btn btn-sm" style={{ border: '1px solid white', color: 'white', background: 'transparent' }} onClick={() => setEditingBasic(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-sm" style={{ border: '1px solid white', color: 'white', background: 'transparent' }} onClick={handleEditBasic}>Edit</button>
            )}
          </div>
          <div className="card-body" style={{ fontSize: '0.9rem' }}>
            {editingBasic ? (
              <div className="row g-2">
                <h6 className="text-success mb-2">👤 परिवार की जानकारी</h6>
                <div className="col-md-3"><label>मुखिया का नाम</label>
                  <input className="form-control" value={basicData.headName || ''} onChange={e => setBasic('headName', e.target.value)} />
                </div>
                <div className="col-md-3"><label>प्रचलित नाम</label>
                  <input className="form-control" value={basicData.displayName || ''} onChange={e => setBasic('displayName', e.target.value)} />
                </div>
                <div className="col-md-3"><label>मोबाइल</label>
                  <input className="form-control" value={basicData.mobile || ''} onChange={e => setBasic('mobile', e.target.value)} />
                </div>
                <div className="col-md-3"><label>कुल सदस्य</label>
                  <input type="number" className="form-control" value={basicData.Totalmember || ''} onChange={e => setBasic('Totalmember', e.target.value)} />
                </div>
                <div className="col-md-3"><label>मातृभाषा</label>
                  <select className="form-select" value={basicData.language || ''} onChange={e => setBasic('language', e.target.value)}>
                    <option value="">चुनें</option>
                    <option>हिंदी</option><option>गुजराती</option><option>मारवाड़ी</option>
                    <option>राजस्थानी</option><option>मराठी</option><option>पंजाबी</option><option>अन्य</option>
                  </select>
                </div>
                <div className="col-md-3"><label>परिवार की स्थिति</label>
                  <select className="form-select" value={basicData.familyType || ''} onChange={e => setBasic('familyType', e.target.value)}>
                    <option value="">चुनें</option>
                    <option>संयुक्त परिवार</option><option>एकल परिवार</option><option>विस्तारित परिवार</option>
                  </select>
                </div>
                <div className="col-md-3"><label>आवास की स्थिति</label>
                  <select className="form-select" value={basicData.houseType || ''} onChange={e => setBasic('houseType', e.target.value)}>
                    <option value="">चुनें</option>
                    <option>स्वयं का मकान</option><option>किराए का मकान</option><option>सरकारी आवास</option><option>अन्य</option>
                  </select>
                </div>
                <h6 className="text-primary mb-2 mt-3">📍 स्थान</h6>
                <div className="col-md-3"><label>राज्य</label>
                  <select className="form-select" value={basicData.state || ''} onChange={e => { setBasic('state', e.target.value); setBasic('city', ''); setBasic('Mandirtype', ''); }}>
                    <option value="">चुनें</option>
                    {states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>शहर</label>
                  <select className="form-select" value={basicData.city || ''} onChange={e => { setBasic('city', e.target.value); setBasic('Mandirtype', ''); }}>
                    <option value="">चुनें</option>
                    {cities.filter(c => c.state === basicData.state).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>ब्लॉक</label>
                  <select className="form-select" value={basicData.block || ''} onChange={e => setBasic('block', e.target.value)}>
                    <option value="">चुनें</option>
                    {blocks.filter(b => b.city === basicData.city).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>शहरी / ग्रामीण</label>
                  <select className="form-select" value={basicData.areaType || ''} onChange={e => setBasic('areaType', e.target.value)}>
                    <option value="">चुनें</option>
                    <option value="Urban">शहरी</option>
                    <option value="Rural">ग्रामीण</option>
                  </select>
                </div>
                {basicData.areaType === 'Urban' && (
                  <div className="col-md-3"><label>शहरी क्षेत्र</label>
                    <select className="form-select" value={basicData.urbanArea || ''} onChange={e => setBasic('urbanArea', e.target.value)}>
                      <option value="">चुनें</option>
                      {urbanAreas.filter(u => u.city === basicData.city).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  </div>
                )}
                {basicData.areaType === 'Rural' && (
                  <>
                    <div className="col-md-3"><label>ग्राम पंचायत</label>
                      <select className="form-select" value={basicData.panchayat || ''} onChange={e => setBasic('panchayat', e.target.value)}>
                        <option value="">चुनें</option>
                        {panchayats.filter(p => p.block === basicData.block).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3"><label>ग्राम</label>
                      <select className="form-select" value={basicData.gram || ''} onChange={e => setBasic('gram', e.target.value)}>
                        <option value="">चुनें</option>
                        {grams.filter(g => g.panchayatName === basicData.panchayat).map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div className="col-12"><label>पता</label>
                  <input className="form-control" value={basicData.address || ''} onChange={e => setBasic('address', e.target.value)} />
                </div>

                <h6 className="text-success mb-2 mt-3">🕌 मंदिर</h6>
                <div className="col-md-6"><label>जैन मंदिर</label>
                  <select className="form-select" value={basicData.Mandirtype || ''} onChange={e => setBasic('Mandirtype', e.target.value)}>
                    <option value="">चुनें</option>
                    {mandirs.filter(m => !basicData.city || m.district === basicData.city).map((m, i) => (
                      <option key={i} value={m.nickName}>
                        {m.nickName}{m.pendingApproval === true ? ' ⏳' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <h6 className="text-warning mb-2 mt-3">🏷️ जाति</h6>
                <div className="col-md-3"><label>जाति प्रकार</label>
                  <select className="form-select" value={basicData.jatiPrakar || ''} onChange={e => { setBasic('jatiPrakar', e.target.value); setBasic('upjatiPrakar', ''); setBasic('gotra', ''); setBasic('moor', ''); }}>
                    <option value="">चुनें</option>
                    {jatiPrakars.map(j => <option key={j.id} value={j.name}>{j.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>उपजाति प्रकार</label>
                  <select className="form-select" value={basicData.upjatiPrakar || ''} onChange={e => { setBasic('upjatiPrakar', e.target.value); setBasic('gotra', ''); setBasic('moor', ''); }} disabled={!basicData.jatiPrakar}>
                    <option value="">चुनें</option>
                    {upjatiPrakars.filter(u => u.jatiPrakar === basicData.jatiPrakar).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>गोत्र / वंश</label>
                  <select className="form-select" value={basicData.gotra || ''} onChange={e => { setBasic('gotra', e.target.value); setBasic('moor', ''); }} disabled={!basicData.upjatiPrakar}>
                    <option value="">चुनें</option>
                    {gotras.filter(g => g.jatiPrakar === basicData.jatiPrakar && g.upjatiPrakar === basicData.upjatiPrakar).map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><label>मूर</label>
                  <select className="form-select" value={basicData.moor || ''} onChange={e => setBasic('moor', e.target.value)} disabled={!basicData.gotra}>
                    <option value="">चुनें</option>
                    {moors.filter(m => m.jatiPrakar === basicData.jatiPrakar && m.upjatiPrakar === basicData.upjatiPrakar && m.gotra === basicData.gotra).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="row g-2">
                <div className="col-md-3"><strong>मुखिया:</strong> {selectedFamily.basic?.headName || '-'}</div>
                <div className="col-md-3"><strong>प्रचलित नाम:</strong> {selectedFamily.basic?.displayName || '-'}</div>
                <div className="col-md-3"><strong>मोबाइल:</strong> {selectedFamily.basic?.mobile || '-'}</div>
                <div className="col-md-3"><strong>कुल सदस्य:</strong> {selectedFamily.basic?.Totalmember || '-'}</div>
                <div className="col-md-3"><strong>मातृभाषा:</strong> {selectedFamily.basic?.language || '-'}</div>
                <div className="col-md-3"><strong>परिवार स्थिति:</strong> {selectedFamily.basic?.familyType || '-'}</div>
                <div className="col-md-3"><strong>आवास:</strong> {selectedFamily.basic?.houseType || '-'}</div>
                <div className="col-md-3"><strong>राज्य:</strong> {selectedFamily.basic?.state || '-'}</div>
                <div className="col-md-3"><strong>शहर:</strong> {selectedFamily.basic?.city || '-'}</div>
                <div className="col-md-3"><strong>ब्लॉक:</strong> {selectedFamily.basic?.block || '-'}</div>
                <div className="col-md-3"><strong>क्षेत्र:</strong> {selectedFamily.basic?.areaType || '-'}</div>
                <div className="col-md-3"><strong>मंदिर:</strong> {selectedFamily.basic?.Mandirtype || '-'}{mandirs.find(m => m.nickName === selectedFamily.basic?.Mandirtype)?.pendingApproval === true ? ' ⏳' : ''}</div>
                <div className="col-md-3"><strong>जाति:</strong> {selectedFamily.basic?.jatiPrakar || '-'}</div>
                <div className="col-md-3"><strong>उपजाति:</strong> {selectedFamily.basic?.upjatiPrakar || '-'}</div>
                <div className="col-md-3"><strong>गोत्र:</strong> {selectedFamily.basic?.gotra || '-'}</div>
                <div className="col-md-3"><strong>मूर:</strong> {selectedFamily.basic?.moor || '-'}</div>
                <div className="col-12"><strong>पता:</strong> {selectedFamily.basic?.address || '-'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="card shadow-sm">
          <div className="card-header" style={{ backgroundColor: '#17a2b8', padding: '1rem' }}>
            <h5 className="mb-0 text-white fw-bold">👥 Members ({Object.keys(selectedFamily.members || {}).length})</h5>
          </div>
          <div className="card-body p-3">
            {Object.entries(selectedFamily.members || {}).map(([memberId, member]) => {
              const memberApproved = member.approved;
              return (
                <div key={memberId} className="card mb-3 shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#1e293b', padding: '0.75rem 1rem' }}>
                    <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: '600', color: 'white' }}>
                      {member.name} 
                      {memberApproved && <span className="badge bg-success ms-2" style={{ fontSize: '0.8rem' }}>✅ Approved</span>}
                    </h6>
                    <div>
                      {!memberApproved && (
                        <>
                          <button className="btn btn-sm me-2" onClick={() => handleApproveMember(memberId)} style={{ fontWeight: '600', backgroundColor: '#28a745', color: 'white', border: 'none' }}>Approve Member</button>
                          <button className="btn btn-sm me-2" onClick={() => handleRejectMember(memberId)} style={{ fontWeight: '600', backgroundColor: '#dc3545', color: 'white', border: 'none' }}>Reject Member</button>
                        </>
                      )}
                      {editingMember === memberId ? (
                        <>
                          <button className="btn btn-sm me-2" onClick={() => handleSaveMember(memberId)} style={{ fontWeight: '600', backgroundColor: '#007bff', color: 'white', border: 'none' }}>Save</button>
                          <button className="btn btn-sm" onClick={() => setEditingMember(null)} style={{ fontWeight: '600', backgroundColor: '#6c757d', color: 'white', border: 'none' }}>Cancel</button>
                        </>
                      ) : (
                        <button className="btn btn-sm" onClick={() => handleEditMember(memberId, member)} style={{ fontWeight: '600', border: '1px solid white', color: 'white', background: 'transparent' }}>Edit</button>
                      )}
                    </div>
                  </div>
                  <div className="card-body" style={{ fontSize: '0.9rem' }}>
                    {editingMember === memberId ? (
                      <div>
                        {/* Personal Information */}
                        <h6 className="text-primary mb-3">👤 व्यक्तिगत जानकारी</h6>
                        <div className="row g-2 mb-3">
                          <div className="col-md-3"><label>नाम *</label><input className="form-control" value={editedData.name || ''} onChange={(e) => setEditedData({...editedData, name: e.target.value})} /></div>
                          <div className="col-md-3"><label>पिता/पति का नाम</label><input className="form-control" value={editedData.fatherName || ''} onChange={(e) => setEditedData({...editedData, fatherName: e.target.value})} /></div>
                          <div className="col-md-2"><label>लिंग</label><select className="form-select" value={editedData.gender || ''} onChange={(e) => setEditedData({...editedData, gender: e.target.value})}><option value="">चुनें</option><option>पुरुष</option><option>महिला</option><option>अन्य</option></select></div>
                          <div className="col-md-2"><label>मुखिया से संबंध *</label><select className="form-select" value={editedData.relation || ''} onChange={(e) => setEditedData({...editedData, relation: e.target.value})}><option value="">चुनें</option>{relationshipOptions.map((r,i) => <option key={i} value={r}>{r}</option>)}</select></div>
                          <div className="col-md-2"><label>जन्म तिथि</label><input type="date" className="form-control" value={editedData.dob || ''} onChange={(e) => setEditedData({...editedData, dob: e.target.value})} /></div>
                          <div className="col-md-3"><label>Aadhaar</label><input className="form-control" value={editedData.aadhaar || ''} onChange={(e) => setEditedData({...editedData, aadhaar: e.target.value})} /></div>
                          <div className="col-md-3"><label>वैवाहिक स्थिति</label><select className="form-select" value={editedData.maritalStatus || ''} onChange={(e) => setEditedData({...editedData, maritalStatus: e.target.value})}><option value="">चुनें</option><option>अविवाहित</option><option>विवाहित</option><option>विधवा/विधुर</option><option>तलाकशुदा</option></select></div>
                          <div className="col-md-2"><label>ब्लड ग्रुप</label><select className="form-select" value={editedData.bloodGroup || ''} onChange={(e) => setEditedData({...editedData, bloodGroup: e.target.value})}><option value="">चुनें</option><option>A+</option><option>A-</option><option>B+</option><option>B-</option><option>AB+</option><option>AB-</option><option>O+</option><option>O-</option></select></div>
                        </div>
                        
                        {/* Contact Information */}
                        <h6 className="text-success mb-3">📞 संपर्क जानकारी</h6>
                        <div className="row g-2 mb-3">
                          <div className="col-md-4"><label>मोबाइल</label><input className="form-control" value={editedData.mobile || ''} onChange={(e) => setEditedData({...editedData, mobile: e.target.value})} /></div>
                          <div className="col-md-4"><label>WhatsApp</label><input className="form-control" value={editedData.whatsapp || ''} onChange={(e) => setEditedData({...editedData, whatsapp: e.target.value})} /></div>
                          <div className="col-md-4"><label>ईमेल</label><input type="email" className="form-control" value={editedData.email || ''} onChange={(e) => setEditedData({...editedData, email: e.target.value})} /></div>
                        </div>
                        
                        {/* Address Information */}
                        <h6 className="text-warning mb-3">🏠 पता जानकारी</h6>
                        <div className="row g-2 mb-3">
                          <div className="col-md-6"><label>वर्त्तमान पता</label><input className="form-control" value={editedData.currentAddress || ''} onChange={(e) => setEditedData({...editedData, currentAddress: e.target.value})} /></div>
                          <div className="col-md-6"><label>जन्म स्थान</label><input className="form-control" value={editedData.birthAddress || ''} onChange={(e) => setEditedData({...editedData, birthAddress: e.target.value})} /></div>
                        </div>
                        
                        {/* Education Information */}
                        <h6 className="text-info mb-3">📚 शिक्षा जानकारी</h6>
                        <div className="row g-2 mb-3">
                          <div className="col-md-3"><label>शिक्षा स्थिति</label><select className="form-select" value={editedData.educationStatus || ''} onChange={(e) => setEditedData({...editedData, educationStatus: e.target.value})}><option value="">चुनें</option><option>Running</option><option>Complete</option></select></div>
                          <div className="col-md-3"><label>राज्य</label><select className="form-select" value={editedData.educationState || ''} onChange={(e) => setEditedData({...editedData, educationState: e.target.value})}><option value="">चुनें</option>{states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                          <div className="col-md-3"><label>जिला</label><select className="form-select" value={editedData.educationDistrict || ''} onChange={(e) => setEditedData({...editedData, educationDistrict: e.target.value})}><option value="">चुनें</option>{cities.filter(c => c.state === editedData.educationState).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                          <div className="col-md-3"><label>कक्षा</label><input className="form-control" value={editedData.educationClass || ''} onChange={(e) => setEditedData({...editedData, educationClass: e.target.value})} /></div>
                          <div className="col-md-12"><label>विषय</label><input className="form-control" value={editedData.educationSubject || ''} onChange={(e) => setEditedData({...editedData, educationSubject: e.target.value})} /></div>
                        </div>
                        
                        {/* Work Information */}
                        <h6 className="text-danger mb-3">💼 कार्य जानकारी</h6>
                        <div className="row g-2">
                          <div className="col-md-3"><label>कार्य प्रकार</label><select className="form-select" value={editedData.workType || ''} onChange={(e) => setEditedData({...editedData, workType: e.target.value})}><option value="">चुनें</option>{workTypes.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}</select></div>
                          <div className="col-md-3"><label>राज्य</label><select className="form-select" value={editedData.workState || ''} onChange={(e) => setEditedData({...editedData, workState: e.target.value})}><option value="">चुनें</option>{states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                          <div className="col-md-3"><label>जिला</label><select className="form-select" value={editedData.workDistrict || ''} onChange={(e) => setEditedData({...editedData, workDistrict: e.target.value})}><option value="">चुनें</option>{cities.filter(c => c.state === editedData.workState).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                          <div className="col-md-3"><label>क्षेत्र</label><select className="form-select" value={editedData.workArea || ''} onChange={(e) => setEditedData({...editedData, workArea: e.target.value})}><option value="">चुनें</option>{urbanAreas.filter(u => u.city === editedData.workDistrict).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}</select></div>
                          <div className="col-md-6"><label>विभाग/प्रकार</label><input className="form-control" value={editedData.workDepartment || ''} onChange={(e) => setEditedData({...editedData, workDepartment: e.target.value})} /></div>
                          <div className="col-md-6"><label>पद/नाम</label><input className="form-control" value={editedData.workPosition || ''} onChange={(e) => setEditedData({...editedData, workPosition: e.target.value})} /></div>
                        </div>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-3"><strong>Father/Husband:</strong> {member.fatherName || '-'}</div>
                        <div className="col-md-2"><strong>Gender:</strong> {member.gender || '-'}</div>
                        <div className="col-md-2"><strong>Relation:</strong> {member.relation || '-'}</div>
                        <div className="col-md-2"><strong>DOB:</strong> {member.dob || '-'}</div>
                        <div className="col-md-3"><strong>Mobile:</strong> {member.mobile || '-'}</div>
                        <div className="col-md-3"><strong>Email:</strong> {member.email || '-'}</div>
                        <div className="col-md-3"><strong>Aadhaar:</strong> {member.aadhaar || '-'}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Confirmation Popup */}
        {confirmAction.show && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="card" style={{ minWidth: '400px' }}>
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Confirm Action</h5>
              </div>
              <div className="card-body">
                <p>{confirmAction.message}</p>
                {(confirmAction.type === 'reject' || confirmAction.type === 'hold' || confirmAction.type === 'request_changes') && (
                  <div className="mb-3">
                    <label className="form-label">Reason <span className="text-danger">*</span></label>
                    <textarea className="form-control" rows="3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason..." />
                  </div>
                )}
              </div>
              <div className="card-footer">
                <button className="btn btn-secondary me-2" onClick={() => { setConfirmAction({ show: false, type: '', message: '' }); setReason(''); }}>Cancel</button>
                <button 
                  className="btn btn-primary" 
                  disabled={(confirmAction.type === 'reject' || confirmAction.type === 'hold' || confirmAction.type === 'request_changes') && !reason}
                  onClick={() => {
                    if (confirmAction.type === 'approve') handleApproveFamily();
                    else if (confirmAction.type === 'approve_member') confirmApproveMember();
                    else if (confirmAction.type === 'reject_member') confirmRejectMember();
                    else handleAction(confirmAction.type);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container-fluid my-4" style={{ maxWidth: '1400px' }}>
      <div className="card shadow-sm">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '1.5rem' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0 text-white fw-bold">✏️ Edit & Approval Management</h4>
            <div className="btn-group" role="group">
              <button className={`btn btn-sm ${filter === 'all' ? 'btn-light fw-bold' : 'btn-outline-light'}`} onClick={() => setFilter('all')}>All ({registrations.length})</button>
              <button className={`btn btn-sm ${filter === 'pending' ? 'btn-warning text-dark fw-bold' : 'btn-outline-light'}`} onClick={() => setFilter('pending')}>Pending ({registrations.filter(r => !r.meta?.status || r.meta?.status === 'pending').length})</button>
              <button className={`btn btn-sm ${filter === 'approved' ? 'btn-success fw-bold' : 'btn-outline-light'}`} onClick={() => setFilter('approved')}>Approved ({registrations.filter(r => r.meta?.status === 'approved').length})</button>
              <button className={`btn btn-sm ${filter === 'hold' ? 'btn-secondary fw-bold' : 'btn-outline-light'}`} onClick={() => setFilter('hold')}>Hold ({registrations.filter(r => r.meta?.status === 'hold').length})</button>
              <button className={`btn btn-sm ${filter === 'rejected' ? 'btn-danger fw-bold' : 'btn-outline-light'}`} onClick={() => setFilter('rejected')}>Rejected ({registrations.filter(r => r.meta?.status === 'rejected').length})</button>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {filteredRegs.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">No registrations found</h5>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.95rem' }}>
                <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <tr>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>#</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>Family ID</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>Head Name</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>Mobile</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>State</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>City</th>
                    <th className="px-3 py-3 text-center" style={{ fontWeight: '600' }}>Members</th>
                    <th className="px-3 py-3 text-center" style={{ fontWeight: '600' }}>Status</th>
                    <th className="px-3 py-3" style={{ fontWeight: '600' }}>Date</th>
                    <th className="px-3 py-3 text-center" style={{ fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegs.map((reg, index) => {
                    const status = reg.meta?.status || 'pending';
                    return (
                      <tr key={reg.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td className="px-3 py-3">{index + 1}</td>
                        <td className="px-3 py-3"><span className="badge bg-secondary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>{reg.id}</span></td>
                        <td className="px-3 py-3" style={{ fontWeight: '500' }}>{reg.basic?.headName || '-'}</td>
                        <td className="px-3 py-3">{reg.basic?.mobile || '-'}</td>
                        <td className="px-3 py-3">{reg.basic?.state || '-'}</td>
                        <td className="px-3 py-3">{reg.basic?.city || '-'}</td>
                        <td className="px-3 py-3 text-center"><span className="badge bg-info" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>{Object.keys(reg.members || {}).length}</span></td>
                        <td className="px-3 py-3 text-center">
                          <span className={`badge ${
                            status === 'approved' ? 'bg-success' :
                            status === 'rejected' ? 'bg-danger' :
                            status === 'hold' ? 'bg-secondary' :
                            status === 'request_changes' ? 'bg-warning text-dark' : 'bg-warning text-dark'
                          }`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem', minWidth: '100px' }}>
                            {status === 'approved' ? '✅ Approved' :
                             status === 'rejected' ? '❌ Rejected' :
                             status === 'hold' ? '⏸ Hold' :
                             status === 'request_changes' ? '⚠ Changes Req.' : '⏳ Pending'}
                          </span>
                        </td>
                        <td className="px-3 py-3">{reg.meta?.createdAt ? new Date(reg.meta.createdAt).toLocaleDateString('en-IN') : '-'}</td>
                        <td className="px-3 py-3 text-center">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setSelectedFamily(reg)}
                            style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem', fontWeight: '500' }}
                          >
                            View/Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditApproval;
